import { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { X, Calendar, ImageIcon } from "lucide-react";

const TYPE_COLORS = {
  event:        { bg:"rgba(59,130,246,0.15)",  color:"#3b82f6", label:"Event" },
  announcement: { bg:"rgba(245,158,11,0.15)",  color:"#f59e0b", label:"Announcement" },
  drive:        { bg:"rgba(34,197,94,0.15)",   color:"#22c55e", label:"Drive" },
  camp:         { bg:"rgba(168,85,247,0.15)",  color:"#a855f7", label:"Camp" },
  urgent:       { bg:"rgba(239,68,68,0.15)",   color:"#ef4444", label:"Urgent" },
};

export default function ViewPosters() {
  const [posters,  setPosters]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter,   setFilter]   = useState("all");

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(
          query(collection(db, "announcements"), orderBy("createdAt", "desc"))
        );
        setPosters(snap.docs.map(d => ({ id:d.id, ...d.data() })).filter(d => d.active !== false));
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const types    = ["all", ...Object.keys(TYPE_COLORS)];
  const filtered = filter === "all" ? posters : posters.filter(p => p.type === filter);

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <ImageIcon size={22} color="#f59e0b"/>
        <div>
          <h2 style={s.title}>Events &amp; Announcements</h2>
          <p style={s.sub}>Latest updates from Help Them Shine Trust</p>
        </div>
        <span style={s.countBadge}>{posters.length} posted</span>
      </div>

      {/* Filter Pills */}
      <div style={s.filterRow}>
        {types.map(t => {
          const tc = TYPE_COLORS[t];
          return (
            <button key={t} onClick={() => setFilter(t)} style={{
              padding:"7px 16px", borderRadius:50, border:"1px solid",
              borderColor: filter===t ? (tc?.color || "#f59e0b") : "rgba(255,255,255,0.08)",
              background:  filter===t ? (tc?.bg    || "rgba(245,158,11,0.15)") : "transparent",
              color:       filter===t ? (tc?.color || "#f59e0b") : "#78716c",
              fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
              textTransform:"capitalize", transition:"all 0.15s",
            }}>
              {t === "all" ? `All (${posters.length})` : `${tc?.label} (${posters.filter(p=>p.type===t).length})`}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div style={s.empty}>Loading announcements...</div>
      ) : filtered.length === 0 ? (
        <div style={s.emptyState}>
          <ImageIcon size={48} color="#1a1714" style={{ marginBottom:14 }}/>
          <div style={{ fontSize:15, fontWeight:600, color:"#52504d", marginBottom:6 }}>
            {filter === "all" ? "No announcements yet" : `No ${filter} announcements`}
          </div>
          <div style={{ fontSize:13, color:"#3a3835" }}>
            Check back later for updates from the admin team
          </div>
        </div>
      ) : (
        <div style={s.grid}>
          {filtered.map(poster => {
            const tc = TYPE_COLORS[poster.type] || TYPE_COLORS.event;
            return (
              <div key={poster.id} onClick={() => setSelected(poster)} style={s.card}>
                {/* Image */}
                <div style={{ position:"relative", paddingTop:"62%", overflow:"hidden", background:"#1a1714" }}>
                  <img
                    src={poster.imageUrl}
                    alt={poster.title}
                    style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", transition:"transform 0.35s" }}
                    onMouseEnter={e => e.target.style.transform = "scale(1.06)"}
                    onMouseLeave={e => e.target.style.transform = "scale(1)"}
                  />
                  {/* Type badge */}
                  <div style={{ position:"absolute", top:10, left:10, background:tc.color, color:"#fff", fontSize:10, fontWeight:800, padding:"3px 10px", borderRadius:50, textTransform:"uppercase", letterSpacing:"0.5px" }}>
                    {tc.label}
                  </div>
                </div>

                {/* Info */}
                <div style={{ padding:"14px 16px 18px" }}>
                  <h3 style={{ fontSize:15, fontWeight:700, color:"#fef3e2", margin:"0 0 8px", lineHeight:1.4 }}>
                    {poster.title}
                  </h3>
                  {poster.date && (
                    <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:"#78716c", marginBottom:6 }}>
                      <Calendar size={12} color="#f59e0b"/> {poster.date}
                    </div>
                  )}
                  {poster.description && (
                    <p style={{ fontSize:12, color:"#78716c", lineHeight:1.6, margin:"0 0 10px", overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                      {poster.description}
                    </p>
                  )}
                  <div style={{ fontSize:12, color:"#f59e0b", fontWeight:700 }}>
                    View Full Poster →
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Screen Modal */}
      {selected && (
        <div style={s.overlay} onClick={() => setSelected(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} style={s.closeBtn}><X size={18}/></button>
            <img
              src={selected.imageUrl}
              alt={selected.title}
              style={{ width:"100%", maxHeight:"65vh", objectFit:"contain", borderRadius:10, marginBottom:16 }}
            />
            <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10 }}>
              <span style={{
                background: (TYPE_COLORS[selected.type]||TYPE_COLORS.event).color,
                color:"#fff", fontSize:10, fontWeight:800,
                padding:"3px 10px", borderRadius:50, textTransform:"uppercase",
              }}>
                {(TYPE_COLORS[selected.type]||TYPE_COLORS.event).label}
              </span>
              {selected.date && (
                <span style={{ fontSize:12, color:"#78716c", display:"flex", alignItems:"center", gap:4 }}>
                  <Calendar size={12}/> {selected.date}
                </span>
              )}
            </div>
            <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 8px" }}>
              {selected.title}
            </h3>
            {selected.description && (
              <p style={{ fontSize:14, color:"#a8a29e", lineHeight:1.7, margin:0 }}>
                {selected.description}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  header:     { display:"flex", alignItems:"center", gap:14, marginBottom:20, background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18, flexWrap:"wrap" },
  title:      { fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 4px" },
  sub:        { fontSize:13, color:"#78716c", margin:0 },
  countBadge: { marginLeft:"auto", fontSize:11, background:"rgba(245,158,11,0.12)", color:"#f59e0b", border:"1px solid rgba(245,158,11,0.25)", padding:"4px 12px", borderRadius:50, fontWeight:700 },
  filterRow:  { display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 },
  grid:       { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 },
  card:       { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden", cursor:"pointer", transition:"transform 0.2s, border-color 0.2s" },
  empty:      { fontSize:13, color:"#52504d", textAlign:"center", padding:"40px 0" },
  emptyState: { textAlign:"center", padding:"60px 20px", background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14 },
  overlay:    { position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(12px)" },
  modal:      { background:"#111009", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, padding:24, width:"100%", maxWidth:600, position:"relative", maxHeight:"92vh", overflowY:"auto" },
  closeBtn:   { position:"absolute", top:14, right:14, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"50%", width:34, height:34, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:"#a8a29e", zIndex:1 },
};
